import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import InfoIcon from "@mui/icons-material/Info";
import Typography from "@mui/material/Typography";
import { getGroupGraph, clearGroupGraph } from "../../../actions/groupTemplateActions";
import QuestionViewerExpandableHook from "../questions/QuestionViewerExpandableHook";
import ArgoReviewTemplateHook from "../../common/ArgoReviewTemplateHook";
import ArgoPortalPreviewHook from "../../common/ArgoPortalPreviewHook";
import AnswerViewerHook from "../answers/AnswerViewerHook";
import statusFormatter from "../../../utilities/StatusFormatter";
import * as metadataUtils from "../../../utilities/metadata";
import * as TEMPLATE from "../../../constants/templateConstants";
import {expanderClear} from "../../../actions/expanderActions";
import * as portalShared from "../../../portal/shared/metadataConstantsAndUtilities";
import {addDrawerNameToStack, removeDrawerNameToStack} from "../../../actions/drawerStackAction";
import FlagCircleIcon from "@mui/icons-material/FlagCircle";
import GroupViewerExpandableHook from "./GroupViewerExpandableHook";

const GroupReviewHook = (props) => {
    const {
        templateId,
        selectedTemplate,
        selectedTemplateIsGraph,
        setStateCallBack,
        scrollTo,
        width
    } = props;

    const [state, setState] = useState({
        openQuestionReview: false,
        selectedQuestionInfoKey: "",
        selectedQuestionInfoId: "",
        selectedQuestionInfoTemplate: {},
        groupLoaded: false,
        questionExpandedArray: [], // For questions assigned to group
        isExpanded: true,
        buttonTitleSelected: "",
        hidePortalPreview: false,
        questionTemplates: {},
        portalPreviewLoaded: false,
        questionsLoaded: false,
        showGroupBackButton: true,
        initialScroll: false
    });

    const dispatch = useDispatch();

    const fromStoreGroupTemplateGraph = useSelector(state => state.groupTemplateGraph);

    const [groupTemplateGraph, setGroupTemplateGraph] = useState({});

    // ComponentWillMount equivalent
    useEffect(() => {
        dispatch(addDrawerNameToStack("Review", "Group"))

        // componentWillUnmount equivalent
        return () => {
            dispatch(expanderClear("GroupReview"));
            dispatch(clearGroupGraph());
            dispatch(removeDrawerNameToStack())
        };
    },[dispatch]);


    useEffect(() => {

        if ((selectedTemplateIsGraph && selectedTemplate["id"]) || fromStoreGroupTemplateGraph["id"]) {

            let groupGraphClone = portalShared.cloneObj(selectedTemplateIsGraph ? selectedTemplate : fromStoreGroupTemplateGraph);
            let questions = groupGraphClone.questionTemplates;

            let questionKeysArray = [];
            for (let q = 0; q < questions.length; q++) {
                questionKeysArray.push(questions[q]);
            }

            groupGraphClone.questionKeys = questionKeysArray; // Add questionKeys array to graph

            setGroupTemplateGraph(groupGraphClone);
        }
        else {
            if (templateId) {
                dispatch(getGroupGraph(templateId));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, selectedTemplateIsGraph, selectedTemplate, fromStoreGroupTemplateGraph]);


    // Handle initial scroll
    useEffect(() => {

        if (!state.initialScroll && scrollTo) {
            const page = document.getElementById(scrollTo);
            if (page) {
                page.scrollIntoView({ block: 'end',  behavior: 'smooth' });
                setState(prevState => ({
                    ...prevState,
                    initialScroll: true
                }));
            }
        }
    }, [scrollTo, state.initialScroll]);

    const setStateCallback = (stateObj) => {
        setState(prevState => ({
            ...prevState,
            ...stateObj
        }));
    };

    const handleToolbarButtonClick = (event, fieldName, buttonTitle) => {
        if (buttonTitle === 'BACK') {
            setState(prevState => ({
                ...prevState,
                openQuestionReview: false, 
                showGroupBackButton: true
            }));
        }
    };

    // Pass state up to parent if needed
    setStateCallBack && setStateCallBack({showGroupBackButton: state.showGroupBackButton});

    if (state.openQuestionReview) {
        return <ArgoReviewTemplateHook
            templateType={TEMPLATE.TYPE.QUESTION}
            templateKey={state.selectedQuestionInfoKey}
            templateId={state.selectedQuestionInfoId}
            templateObject={state.selectedQuestionInfoTemplate}
            handleToolbarButtonClickFunction={handleToolbarButtonClick}
            width={width}
        />;
    }

    const standardStyle = {
        width: "100%",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "center",
        overflowWrap: "break-word"
    };

    let current = true
    if (groupTemplateGraph?.questionTemplates) {
        current = groupTemplateGraph?.questionTemplates.every(question =>
            question.currentFlag !== false
        );
    }

    return (
        groupTemplateGraph.hasOwnProperty("metadata") ?
            <div id="groupReview-container-div" style={{flexGrow: "2", padding: "32px"}}>

                <div style={{minHeight: "min-content"}}>

                    <Typography variant="h6" component="div">
                        Group Information:
                    </Typography>

                    <div>&nbsp;</div>

                    <div id="GroupReview-complex-question-type-container-div" style={standardStyle}>
                        <div style={{color: portalShared.LABEL_COLOR}}>Status:</div>
                        <div>&nbsp;</div>
                        <div>{statusFormatter(groupTemplateGraph, 0, 0, true, true)}</div>
                        <div>&nbsp;&nbsp;</div>
                        <div style={{
                            color: "red",
                            fontSize: "12px",
                            paddingTop: "2px"
                        }}>{groupTemplateGraph.currentFlag ? "" : "(Not Current Version)"}</div>
                    </div>

                    <div>&nbsp;</div>

                    {!current ?
                        <React.Fragment>
                            <div style={{display: "flex"}}>
                                <FlagCircleIcon style={{color: "#E84343"}}/>
                                <div>&nbsp;</div>
                                <div style={{color: portalShared.colorNameToHex('red')}}>Not all components are current. To update components, republish questionnaire</div>
                            </div>

                            <div>&nbsp;</div>
                        </React.Fragment> : ""
                    }

                    <div id="GroupReview-complex-question-type-container-div" style={standardStyle}>
                        <div style={{color: portalShared.LABEL_COLOR}}>Likert:</div>
                        <div>&nbsp;</div>
                        <div>{groupTemplateGraph.answerOptionTemplates.length ? "Yes" : "No"}</div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="GroupReview-complex-question-name-container-div" style={standardStyle}>
                        <div style={{color: portalShared.LABEL_COLOR}}>Group:</div>
                        <div>&nbsp;</div>
                        <div>{groupTemplateGraph.name}</div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="GroupReview-complex-question-qualifier-container-div" style={standardStyle}>
                        <div style={{color: portalShared.LABEL_COLOR}}>Qualifier:</div>
                        <div>&nbsp;</div>
                        <div>{groupTemplateGraph.nameQualifier}</div>
                    </div>

                    <div>&nbsp;</div>

                    <div id="GroupReview-complex-question-description-container-div" style={standardStyle}>
                        <div style={{color: portalShared.LABEL_COLOR}}>Description:</div>
                        <div>&nbsp;</div>
                        <div>
                            {groupTemplateGraph.description && <div style={standardStyle}>
                                <InfoIcon style={{minWidth: "36px"}}/>
                                <div>&nbsp;</div>
                                <div>{groupTemplateGraph.description}</div>
                            </div>}
                        </div>
                    </div>

                    <div>&nbsp;</div>

                    <div>
                        {metadataUtils.listMetadata(groupTemplateGraph, "Group")}
                    </div>

                </div>

                {/*// ===========================================================================================================*/}
                {/*// GROUP LIKERT ANSWERS */}
                {/*// ===========================================================================================================*/}

                {(groupTemplateGraph.answerOptionTemplates.length > 0) ?
                    <AnswerViewerHook
                        answerOptionTemplates={groupTemplateGraph.answerOptionTemplates}
                        parentsLabel="Group"
                    />
                    : ""
                }

                {/*// ===========================================================================================================*/}
                {/*// ASSIGNED QUESTIONS LIST - NOTE: If (groupTemplateGraph.answerOptionTemplates.length > 0) Group is a LIKERT */}
                {/*// ===========================================================================================================*/}

                <QuestionViewerExpandableHook
                    parentName="GroupReview"
                    parentType={TEMPLATE.TYPE.GROUP}
                    parentTemplate={groupTemplateGraph}
                    questionKeys={groupTemplateGraph.questionKeys}
                    showIconButtonReorder={false}
                    showIconButtonEdit={false}
                    showIconButtonRemove={false}
                    setStateCallback={setStateCallback}
                    hidePortalPreview={(groupTemplateGraph.answerOptionTemplates.length > 0)}
                    isLikert={groupTemplateGraph.answerOptionTemplates.length > 0}
                    useGraph={true}
                    groupGraph={groupTemplateGraph}
                />

                {/*// ===========================================================================================================*/}
                {/*// RELATED SHOW GROUPS LIST */}
                {/*// ===========================================================================================================*/}

                <GroupViewerExpandableHook
                    parentName="CategoryEditor"
                    parentKey=""
                    groupGraph={groupTemplateGraph}
                    groupKeys={groupTemplateGraph.showGroups}
                    setStateCallback={setStateCallback}
                    hidePortalPreview={true}
                    //questionTemplate={questionTemplates}
                    showIconButtonReorder={false}
                    showIconButtonEdit={false}
                    showIconButtonRemove={false}
                    useGraph={true}
                    isShowGroups={true}
                />

                {/*// ===========================================================================================================*/}
                {/*// PORTAL PREVIEW */}
                {/*// ===========================================================================================================*/}

                <div id="GroupReview-space-above-portal-preview" style={{height: "20px"}}>&nbsp;</div>

                <ArgoPortalPreviewHook
                    calledBy="GroupReview"
                    templateType={TEMPLATE.TYPE.GROUP_GRAPH}
                    template={groupTemplateGraph}
                    templateGraph={groupTemplateGraph}
                />

            </div>
            : ""
    );
};

GroupReviewHook.defaultProps = {
    setStateCallBack: function () {},
    scrollTo: "",
    width: "100%",
    groupTemplates: {},
    selectedTemplate: {},
    selectedTemplateIsGraph: false
};

GroupReviewHook.propTypes = {
    questionKeys: PropTypes.array,
    templateKey: PropTypes.string,
    templateId: PropTypes.number,
    selectedTemplate: PropTypes.object,
    selectedTemplateIsGraph: PropTypes.bool,
    groupTemplates: PropTypes.object,
    style: PropTypes.object,
    setStateCallBack: PropTypes.func,
    scrollTo: PropTypes.string,
    width: PropTypes.string
};

export default GroupReviewHook;